import ReactBilling from '@web-solutions/react-billing';
import Attribution from '@web-solutions/module-attribution';

import API, { setBillingBaseUrlOverride } from './api';

import { getExtraParams } from './utils';

const TRUEGATE_PAYMENT_SYSTEM = 'truegate';
const STRIPE_PAYMENT_SYSTEM = 'stripe';

/**
 * FB ad labels (campaign/adset/creative) captured on the landing, forwarded so
 * the cohort report can attribute revenue to a campaign. `{}` for an organic
 * visit — the object is omitted from the body rather than sent empty.
 *
 * Stripe only. The `/payments/*` endpoints are shared with Truegate, whose
 * billing API is third-party and cannot take a new field, so the labels must not
 * leak into those bodies.
 *
 * Idempotent by contract: the backend remembers the first snapshot it ever gets
 * for a user and ignores every later one, and never fails a payment because of
 * it — so sending it on every request is safe and expected.
 */
const getAttributionParams = (paymentSystemType) => {
  if (paymentSystemType !== STRIPE_PAYMENT_SYSTEM) {
    return {};
  }

  const attribution = Attribution.getAdAttribution();

  return attribution ? { attribution } : {};
};

/**
 * Meta Pixel cookies (`_fbp` / `_fbc`) as the top-level `fbp` / `fbc` body keys
 * next to `idfm` — NOT inside `attribution` or `metadata`. Sent with every
 * request that creates a payment, read from `document.cookie` at that moment,
 * so the backend gets the values current at payment time. Missing cookie — no
 * key. Same Stripe-only gate as the attribution: TrueGate's billing API is
 * third-party, for it the keys travel in the order metadata instead.
 */
const getFbPixelCookieParams = (paymentSystemType) => {
  if (paymentSystemType !== STRIPE_PAYMENT_SYSTEM) {
    return {};
  }

  return Attribution.getFbPixelCookies();
};

const unwrapData = (response) => {
  if (response && Object.prototype.hasOwnProperty.call(response, 'data')) {
    return response.data;
  }

  return response;
};

const getFoodzeeApiBaseUrl = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const prodUrl = process.env.REACT_APP_FOODZEE_PROD_URL;
  const isProd = prodUrl && currentUrl.startsWith(prodUrl) && !currentUrl.includes('stage');

  return isProd
    ? process.env.REACT_APP_API_FOODZE_BASE_URL_PROD
    : process.env.REACT_APP_API_FOODZE_BASE_URL_STAGE;
};

const Billing = {
  paymentSystemProject: '',
  paymentSystemType: '',

  setApiAuth(apiAuth) {
    this.apiAuth = apiAuth;
  },

  getPaymentParams({ paymentSystem, paymentProject } = {}) {
    return {
      payment_system_type: paymentSystem || this.paymentSystemType,
      payment_system_project: paymentProject || this.paymentSystemProject,
    };
  },

  getHeaders() {
    return {
      Authorization: this.apiAuth,
    };
  },

  /**
   * Restore the module after a page reload without a network round-trip:
   * payment system / project and the API host they imply. Public keys are NOT
   * restored — call init() before anything that needs Stripe.js (3-D Secure,
   * the card form). Enough for a server-side charge such as one-time-payment.
   */
  restore(paymentSystem, paymentProject) {
    console.log('[Billing] restore', { paymentSystem, paymentProject });
    this.paymentSystemType = paymentSystem;
    this.paymentSystemProject = paymentProject;
    this.applyBaseUrlOverride(paymentSystem);
  },

  applyBaseUrlOverride(paymentSystem) {
    if (paymentSystem === STRIPE_PAYMENT_SYSTEM) {
      const stripeBillingUrl = getFoodzeeApiBaseUrl();
      console.log('[Billing] setting Stripe billing URL override:', stripeBillingUrl);
      setBillingBaseUrlOverride(stripeBillingUrl);
    } else if (
      paymentSystem === TRUEGATE_PAYMENT_SYSTEM &&
      process.env.REACT_APP_API_TRUEGATE_BILLING_URL
    ) {
      console.log('[Billing] setting Truegate billing URL override:', process.env.REACT_APP_API_TRUEGATE_BILLING_URL);
      setBillingBaseUrlOverride(process.env.REACT_APP_API_TRUEGATE_BILLING_URL);
    } else {
      console.log('[Billing] no URL override (paymentSystem=', paymentSystem, ', envSet=', !!process.env.REACT_APP_API_TRUEGATE_BILLING_URL, ')');
      setBillingBaseUrlOverride(null);
    }
  },

  async init(paymentSystem, paymentProject) {
    console.log('[Billing] init', { paymentSystem, paymentProject });
    this.paymentSystemType = paymentSystem;
    this.paymentSystemProject = paymentProject;
    this.applyBaseUrlOverride(paymentSystem);

    const headers = this.getHeaders();

    try {
      const keys = await API.getPublicKey(this.getPaymentParams(), headers);
      return ReactBilling.init(paymentSystem, keys);
    } catch (e) {
      console.error('[Billing] init: getPublicKey FAILED', e);
      throw e;
    }
  },

  async getPrices(pricesIds, { paymentSystem } = {}) {
    const headers = this.getHeaders();
    const params = {
      search: `external_id:${pricesIds.length !== 0 ? pricesIds.join(','): '40_foodzee_5_io3_month,20_foodzee_2_io3_month'}`,
      searchFields: 'external_id:in',
      ...this.getPaymentParams({ paymentSystem }),
    };
    return unwrapData(await API.getPrices(params, headers)) || [];
  },

  async createOrder({
    one_time_product,
    one_time_products,
    productId,
    orderDescription,
    products,
    currency,
    paymentSystem,
    trigger,
    metadata,
    solidMetadata,
    is_sending_terminate_links,
    is_sending_trial_end
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      one_time_product,
      trigger,
      metadata: JSON.stringify(metadata),
      solid_metadata: solidMetadata,
      one_time_products: one_time_products || undefined,
      product_id: productId,
      order_description: orderDescription,
      products,
      currency,
      is_sending_terminate_links,
      is_sending_trial_end
    };

    return API.createOrder(body, this.getHeaders());
  },

  async initAlternativePayment({
    paymentMethod,
    paymentSystem,
    trigger,
    products,
    currency,
    metadata,
    one_time_products,
    orderDescription,
    is_sending_terminate_links,
    is_sending_trial_end,
    solid_metadata,
    first_name,
    last_name,
    taxpayer_id,
    success_url,
    fail_url,
    return_url,
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      products,
      solid_metadata,
      currency,
      metadata: JSON.stringify(metadata),
      one_time_products,
      trigger,
      order_description: orderDescription,
      is_sending_terminate_links,
      is_sending_trial_end,
      payment_method: paymentMethod,
      customer_first_name: first_name,
      customer_last_name: last_name,
      taxpayer_id,
      success_url,
      fail_url,
      return_url,
    };

    return API.initAlternativePayment(body, this.getHeaders())
  },

  async createCheckout({ productId, checkoutId, paymentMethod, successUrl, paymentSystem }) {
    const extra = await getExtraParams();
    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      product_id: productId,
      checkout_id: checkoutId,
      payment_method: paymentMethod,
      success_url: successUrl,
    };

    return API.createCheckout(body, this.getHeaders());
  },

  async createCustomer({ email, paymentSystem, paymentProject }) {
    const headers = this.getHeaders();
    const paymentParams = this.getPaymentParams({ paymentSystem, paymentProject });
    const extra = paymentParams.payment_system_type === STRIPE_PAYMENT_SYSTEM
      ? await getExtraParams()
      : {};
    const body = {
      ...paymentParams,
      ...getAttributionParams(paymentParams.payment_system_type),
      ...(extra.idfm ? { idfm: extra.idfm } : {}),
      ...(email && { email }),
    };

    return unwrapData(await API.createCustomer(body, headers));
  },

  async subscribe({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = await getExtraParams();
    const paymentParams = this.getPaymentParams({ paymentSystem });

    const body = {
      ...extra,
      ...formData,
      ...paymentParams,
      ...getAttributionParams(paymentParams.payment_system_type),
      ...getFbPixelCookieParams(paymentParams.payment_system_type),
      metadata: JSON.stringify(metadata),
    };

    return unwrapData(await API.createSubscription(body, headers));
  },

  async confirmSubscriptionPayment(subscriptionId) {
    return unwrapData(await API.confirmSubscriptionPayment(subscriptionId, this.getHeaders()));
  },

  async subscribeOnOneClick({ productId, metadata, ...params }) {
    const body = {
      ...this.getPaymentParams(),
      ...params,
      metadata: JSON.stringify(metadata),
      product_id: productId,
    };

    const response = await API.subscribeOnOneClick(body, this.getHeaders())

    return response
  },

  async applePayOrder({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = await getExtraParams();

    const body = {
      ...extra,
      ...formData,
      customer_email: formData.email,
      product_id: formData.price_id,
      metadata: JSON.stringify(metadata),
      ...this.getPaymentParams({ paymentSystem }),
    };

    return API.createApplePayOrder(body, headers);
  },

  async getSubscriptions(isAsb) {
    const headers = this.getHeaders();
    if (isAsb) {
      return API.getAllSubscriptions(headers);
    }
    return API.getActualizedSubscriptions(headers);
  },

  async requestCancel({ email, trigger }) {
    const body = {
      email,
      trigger,
    };

    return API.cancelSubscription(body);
  },

  async submitCancelSubscription(params) {
    return API.submitCancelSubscription(params);
  },

  async createOneClickPayment({
    amount,
    currency,
    force3ds,
    productCode,
    productId,
    paymentMethodId,
    setupFutureUsage,
    transactionId,
    description,
    successUrl,
    failUrl,
    trigger,
    metadata,
    solidMetadata,
    idfm,
    withTransactionDetailsEmail
  }) {
    const headers = this.getHeaders();
    const params = this.getPaymentParams();
    const isStripePayment = params.payment_system_type === STRIPE_PAYMENT_SYSTEM;
    const metadataProductId = isStripePayment
      ? (productCode || productId)
      : (productId || productCode);
    const metadataIdfm = idfm || description;
    const paymentMetadata = isStripePayment
      ? {
        ...(metadata || {}),
        ...(metadataProductId ? { product_id: metadataProductId } : {}),
        ...(metadataIdfm ? { idfm: metadataIdfm } : {}),
      }
      : metadata;

    const body = {
      ...params,
      ...getAttributionParams(params.payment_system_type),
      ...getFbPixelCookieParams(params.payment_system_type),
      withTransactionDetailsEmail,
      force3ds,
      amount,
      description,
      currency,
      trigger,
      ...(isStripePayment && metadataIdfm ? { idfm: metadataIdfm } : {}),
      metadata: JSON.stringify(paymentMetadata),
      solid_metadata: solidMetadata,
      ...(paymentMethodId ? { payment_method_id: paymentMethodId } : {}),
      ...(setupFutureUsage ? { setup_future_usage: setupFutureUsage } : {}),
      success_url: successUrl,
      fail_url: failUrl,
      product_code: productCode,
      ...((params.payment_system_type === TRUEGATE_PAYMENT_SYSTEM || isStripePayment) && {
        product_id: metadataProductId,
      }),
      ...(params.payment_system_type === TRUEGATE_PAYMENT_SYSTEM && {
        transaction_id: transactionId,
      }),
    };

    return API.createOneClickPayment(body, headers);
  },

  async getPaymentMethod() {
    const headers = this.getHeaders();
    const paymentMethod = await API.getPaymentMethod(headers);
    return paymentMethod?.data || paymentMethod;
  },

  async getOneTimePurchases() {
    const headers = this.getHeaders();
    const oneTimePurchases = await API.getOneTimePurchases(headers);
    return oneTimePurchases?.data || oneTimePurchases;
  },

  async sendAccessEmail(email) {
    const headers = this.getHeaders();
    const body = {
      email
    }

    return API.sendAccessEmail(body, headers);
  },

  async changePlan({ subscriptionId, productId, isNewSubscription, successUrl, failUrl }) {
    const headers = this.getHeaders();

    const body = {
      ...this.getPaymentParams(),
      subscriptionId,
      planCode: productId,
      isNewSubscription: !!isNewSubscription,
      success_url: successUrl,
      fail_url: failUrl,
    };

    return API.changePlan(body, headers);
  },

  async giveProduct(productId) {
    const headers = this.getHeaders();

    const body = {
      ...this.getPaymentParams(),
      product_code: productId,
    };

    return API.giveProduct(body, headers);
  }
};

export default Billing;
